import React, { useState } from 'react';
import styled from 'styled-components';
import Window from '../Window';
import { Send, Mail, Phone, MapPin, Github, Linkedin, User } from 'lucide-react';

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f7;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  font-weight: 600;
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
`;

const ContactStatus = styled.div`
  font-size: 12px;
  color: #666;
`;

const ChatArea = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Message = styled.div`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
  
  ${props => props.isUser ? `
    background: #007AFF;
    color: white;
    align-self: flex-end;
    border-bottom-right-radius: 4px;
  ` : `
    background: rgba(255, 255, 255, 0.8);
    color: #333;
    align-self: flex-start;
    border-bottom-left-radius: 4px;
  `}
`;

const ContactCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ContactDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #333;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
`;

const ContactLink = styled.a`
  color: #007AFF;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const InputArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.8);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  font-size: 14px;
  outline: none;
  
  &:focus {
    border-color: #007AFF;
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #007AFF;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  
  &:hover {
    background: #0056CC;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const MessagesApp = ({ ...windowProps }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm Aviral Malik. Thanks for checking out my portfolio! 👋",
      isUser: false,
      timestamp: new Date()
    },
    {
      id: 2,
      text: "I'm a Software Developer passionate about building scalable applications and solving complex problems.",
      isUser: false,
      timestamp: new Date()
    },
    {
      id: 3,
      text: "Feel free to reach out if you'd like to collaborate or have any questions!",
      isUser: false,
      timestamp: new Date()
    }
  ]);

  const handleSendMessage = () => {
    if (message.trim()) {
      const newMessage = {
        id: messages.length + 1,
        text: message,
        isUser: true,
        timestamp: new Date()
      };
      setMessages([...messages, newMessage]);
      setMessage('');
      
      // Simulate response
      setTimeout(() => {
        const response = {
          id: messages.length + 2,
          text: "Thanks for your message! I'll get back to you soon. You can also reach me directly at aviralmalik2002@gmail.com",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, response]);
      }, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <Window {...windowProps} size={{ width: 500, height: 600 }}>
      <MessagesContainer>
        <Header>
          <Avatar>AM</Avatar>
          <ContactInfo>
            <ContactName>Aviral Malik</ContactName>
            <ContactStatus>Online • Software Developer</ContactStatus>
          </ContactInfo>
        </Header>
        
        <ChatArea>
          <ContactCard>
            <ContactDetail>
              <ContactIcon>
                <Mail size={16} />
              </ContactIcon>
              <ContactLink href="mailto:aviralmalik2002@gmail.com">
                aviralmalik2002@gmail.com
              </ContactLink>
            </ContactDetail>
            <ContactDetail>
              <ContactIcon>
                <Phone size={16} />
              </ContactIcon>
              <span>+91-8700662942</span>
            </ContactDetail>
            <ContactDetail>
              <ContactIcon>
                <MapPin size={16} />
              </ContactIcon>
              <span>Manipal, Karnataka, India</span>
            </ContactDetail>
            <ContactDetail>
              <ContactIcon>
                <Github size={16} />
              </ContactIcon>
              <ContactLink href="https://github.com/Floofy244" target="_blank">
                github.com/Floofy244
              </ContactLink>
            </ContactDetail>
            <ContactDetail>
              <ContactIcon>
                <Linkedin size={16} />
              </ContactIcon>
              <ContactLink href="https://linkedin.com/in/Aviral-Malik" target="_blank">
                linkedin.com/in/Aviral-Malik
              </ContactLink>
            </ContactDetail>
          </ContactCard>
          
          {messages.map((msg) => (
            <Message key={msg.id} isUser={msg.isUser}>
              {msg.text}
            </Message>
          ))}
        </ChatArea>
        
      </MessagesContainer>
    </Window>
  );
};

export default MessagesApp;
